import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const bc = { borderColor: "var(--ll-border)" };

// Returns [run, dialog]. run(file, accountId) resolves to the account id to upload into, or null to cancel.
export function useAccountPrecheck() {
  const [state, setState] = useState(null);
  const resolver = useRef(null);
  const run = async (file, accountId) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bank_account_id", accountId);
      const r = await api.post("/statements/account-precheck", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!r.data.mismatch) return accountId;
      return await new Promise((res) => { resolver.current = res; setState({ ...r.data, accountId: r.data.account_id || accountId, fileName: file.name }); });
    } catch {
      return accountId;
    }
  };
  const finish = (v) => { setState(null); resolver.current?.(v); };
  const dialog = state && (
    <Dialog open onOpenChange={(o) => !o && finish(null)}>
      <DialogContent className="max-w-md" data-testid="account-precheck-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-[var(--ll-error)]"><ShieldAlert size={18} /> Is this the right account?</DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]" data-testid="account-precheck-message">
            <b>{state.fileName}</b> looks like account <b>••{state.detected}</b>
            {state.source === "filename" ? " (from the file name)" : " (from the number printed on it)"}, but {state.locked ? "your uploads go to the account ending in" : "you picked an account ending in"} <b>••{state.account_last4}</b>.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {state.match && (
            <button onClick={() => finish(state.match.id)} className="px-4 py-2 text-sm text-white bg-[var(--ll-ink)]" data-testid="account-precheck-switch">
              Switch to {state.match.name}{state.match.bank_name ? ` · ${state.match.bank_name}` : ""} ••{state.match.last4}
            </button>
          )}
          <button onClick={() => finish(state.accountId)} className="px-4 py-2 text-sm border" style={bc} data-testid="account-precheck-continue">
            Upload to ••{state.account_last4} anyway
          </button>
          <button onClick={() => finish(null)} className="px-4 py-2 text-sm text-[var(--ll-ink-soft)] hover:underline" data-testid="account-precheck-cancel">Cancel</button>
        </div>
        {state.locked && <div className="text-[11px] text-[var(--ll-ink-muted)]" data-testid="account-precheck-locked">Your accountant set your uploads to go to ••{state.account_last4}. If this file is for another account, cancel and message them.</div>}
        {!state.match && !state.locked && <div className="text-[11px] text-[var(--ll-ink-muted)]">No account ending in ••{state.detected} exists yet. Cancel and add it first if this statement is for a different account.</div>}
      </DialogContent>
    </Dialog>
  );
  return [run, dialog];
}
